import { FC, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '@/hooks/useAuth';
import { Popup } from '@/components/UI/Popup/Popup';
import { cleanPhoneNumber, getLoginUrlWithReturn } from '@/utils/helpers';
import { Drawer } from '@/components/Drawer/Drawer';
import {
  Props,
  EventData,
  EventName,
} from '@/components/AuthIframe/AuthIframe.types';
import { CONFIG } from '@/constants/config';
import useScreenSize from '@/hooks/useScreenSize';
import { CommonTestIds } from '@/__e2e__/testIds/common';

export const AuthIframe: FC<Props> = ({
  phone,
  isOpen,
  close,
  onSuccess,
  onManualClose,
}) => {
  const { isLarge } = useScreenSize();
  const router = useRouter();
  const { loginUser, getBaseUser } = useAuth();

  const authUrl = getLoginUrlWithReturn(
    router.asPath,
    router.asPath,
    phone ? cleanPhoneNumber(phone) : undefined
  );

  const handleMessage = (event: MessageEvent<EventData | EventName>) => {
    const eventHost = new URL(event.origin).host;

    if (eventHost !== CONFIG.authHost) return;

    const eventData = event.data;

    if (
      typeof eventData === 'object' &&
      'access_token' in eventData &&
      eventData?.name === EventName.Finished
    ) {
      loginUser({ variables: { accessToken: eventData?.access_token } }).then(
        () => {
          getBaseUser();
          close();
          onSuccess();
        }
      );
    } else if (event.data === EventName.Closed) {
      handleManualClose();
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [handleMessage]);

  const renderIframe = () =>
    isOpen && (
      <iframe
        id="auth-iframe"
        src={authUrl}
        height="100%"
        width="100%"
        style={{ border: 'none' }}
        title="Authentication"
      />
    );

  function handleManualClose() {
    onManualClose?.();
    close();
  }

  return isLarge ? (
    <Popup
      isOpen={isOpen}
      close={handleManualClose}
      customSize={{ width: 680, height: 580 }}
      dataTestId={CommonTestIds.auth.iframe.popup}
    >
      {renderIframe()}
    </Popup>
  ) : (
    <Drawer
      isOpen={isOpen}
      isHeadHidden
      dataTestId={CommonTestIds.auth.iframe.drawer}
    >
      {renderIframe()}
    </Drawer>
  );
};
